import { z } from "zod";
import { useRoleStore } from "@/lib/stores/role";
import { useAuthStore } from "@/lib/stores/auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions<TBody = unknown> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: TBody;
  headers?: Record<string, string>;
  /** Skip zod validation and return raw parsed JSON */
  skipValidation?: boolean;
}

async function request<TResponse>(
  path: string,
  schema: z.ZodType<TResponse>,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { method = "GET", body, headers = {} } = options;

  // Read auth token from the auth store (no React hook — utility fn).
  // TODO(wire-be): token is a real JWT in production; MSW just validates the
  // fixture string. No changes needed here when wiring to real BE.
  let token: string | null = null;
  try {
    token = useAuthStore.getState().token;
  } catch {
    // Store not hydrated yet — skip
  }

  // Fallback: also attach X-Mock-Role for any existing handlers that still
  // check the header rather than the Bearer token. This keeps legacy MSW
  // handlers functional while new auth handlers use Bearer.
  // TODO(wire-be): remove X-Mock-Role header once all handlers validate Bearer.
  let role: string | null = null;
  try {
    role = useRoleStore.getState().currentRole;
  } catch {
    // Store not hydrated yet (SSR) — skip
  }

  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (token) {
    requestHeaders["Authorization"] = `Bearer ${token}`;
  }

  if (role) {
    requestHeaders["X-Mock-Role"] = role;
  }

  const response = await fetch(`/v1/admin${path}`, {
    method,
    headers: requestHeaders,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorCode = "UNKNOWN_ERROR";
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorBody = await response.json();
      errorCode = (errorBody as { code?: string }).code ?? errorCode;
      errorMessage = (errorBody as { message?: string }).message ?? errorMessage;
    } catch {
      // Non-JSON error response
    }

    // 401 → clear session + signal router to redirect to /login.
    // Dispatching a window event decouples the API client from React Router so
    // this pure utility function stays framework-agnostic. ProtectedShell
    // listens for "auth:unauthorized" and navigates to /login (see
    // src/app/ProtectedShell.tsx). The event name is the contract between the
    // two and must stay in sync.
    if (response.status === 401) {
      try {
        useAuthStore.getState().clearSession();
        useRoleStore.getState().logout();
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      } catch {
        // Non-browser environment (tests, SSR) — skip
      }
    }

    throw new ApiError(response.status, errorCode, errorMessage);
  }

  // #221: tolerate 204 No Content / empty bodies. response.json() throws on an
  // empty body, which previously forced callers like logout to bypass apiClient
  // with a raw fetch. Read as text first so 204 / empty responses resolve to
  // undefined instead of throwing; callers that expect no content pass a schema
  // tolerant of undefined (or ignore the result).
  if (response.status === 204) {
    return undefined as TResponse;
  }

  const text = await response.text();
  if (!text) {
    return undefined as TResponse;
  }

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    // ok response with a non-JSON body — hand the raw text back to the caller.
    return text as TResponse;
  }

  if (options.skipValidation) {
    return data as TResponse;
  }

  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    console.warn("[API] Response validation failed:", parsed.error.errors);
    // In demo mode we still return the data to avoid breaking UX
    return data as TResponse;
  }

  return parsed.data;
}

export const apiClient = {
  get<TResponse>(
    path: string,
    schema: z.ZodType<TResponse>,
    params?: Record<string, string | number | boolean | undefined>,
  ) {
    const url = params
      ? `${path}?${new URLSearchParams(
          Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)]),
          ),
        ).toString()}`
      : path;
    return request(url, schema, { method: "GET" });
  },

  post<TResponse, TBody = unknown>(path: string, schema: z.ZodType<TResponse>, body: TBody) {
    return request(path, schema, { method: "POST", body });
  },

  patch<TResponse, TBody = unknown>(path: string, schema: z.ZodType<TResponse>, body: TBody) {
    return request(path, schema, { method: "PATCH", body });
  },

  put<TResponse, TBody = unknown>(path: string, schema: z.ZodType<TResponse>, body: TBody) {
    return request(path, schema, { method: "PUT", body });
  },

  delete<TResponse>(path: string, schema: z.ZodType<TResponse>) {
    return request(path, schema, { method: "DELETE" });
  },
};
